import React from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus } from 'lucide-react';
import { POExtractionPanel } from './POExtractionPanel';
import { POLineItemsEditor } from './POLineItemsEditor';
import { EntityCustomFields } from '../shared/EntityCustomFields';
import { DatePicker } from '../shared/DatePicker';
import { useAuth } from '../../context/AuthContext';

export const POFormDialog = ({ open, onOpenChange, editingPo, formData, setFormData, onSubmit, onNew, parentPoOptions = [], onExtracted, workloadTypeOptions = [], onSchemaChanged, canCreate = true }) => {
  const { user } = useAuth();
  const workloadOptions = workloadTypeOptions.length > 0
    ? workloadTypeOptions
    : [{ value: 'Training Delivery', label: 'Private Delivery' }, { value: 'Content Development', label: 'Content Development' }];
  const isContentWorkload = formData.workload_type === 'Content Development';
  return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogTrigger asChild>
      {canCreate ? (
        <Button onClick={onNew} data-testid="add-po-button" className="bg-lime text-lime-foreground hover:bg-lime/90">
          <Plus className="w-4 h-4 mr-2" />
          New PO
        </Button>
      ) : <span />}
    </DialogTrigger>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{editingPo ? 'Edit Purchase Order' : 'Create Purchase Order'}</DialogTitle>
        <DialogDescription>
          {editingPo
            ? 'Update blanket PO details'
            : isContentWorkload
              ? 'Create a new PO funding content build/refresh activities'
              : 'Create a new blanket PO for training deliveries'}
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="workload_type">Workload Type</Label>
          <Select
            value={formData.workload_type || workloadOptions[0]?.value}
            onValueChange={(v) => setFormData({ ...formData, workload_type: v, ...(editingPo ? {} : { po_type: 'standalone', parent_po_id: '' }) })}
          >
            <SelectTrigger data-testid="po-workload-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {workloadOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!editingPo && (
          <POExtractionPanel poType={formData.po_type === 'extension' ? 'extension' : (isContentWorkload ? 'content' : 'delivery')} onExtracted={onExtracted} initialFilename={formData.document_filename} />
        )}
        {!editingPo && !isContentWorkload && (
          <div className="space-y-2">
            <Label htmlFor="po_type">PO Type</Label>
            <Select value={formData.po_type || 'standalone'} onValueChange={(v) => setFormData({ ...formData, po_type: v })}>
              <SelectTrigger data-testid="po-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standalone">Standalone PO</SelectItem>
                <SelectItem value="extension">Extension to an existing PO</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {formData.po_type === 'extension' && !editingPo && (
          <div className="space-y-2">
            <Label htmlFor="parent_po_id">Parent PO *</Label>
            <Select value={formData.parent_po_id} onValueChange={(v) => setFormData({ ...formData, parent_po_id: v })}>
              <SelectTrigger data-testid="po-parent-select"><SelectValue placeholder="Select parent PO" /></SelectTrigger>
              <SelectContent>
                {parentPoOptions.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.po_number}{p.short_title ? ` — ${p.short_title}` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="po_number">{formData.po_type === 'extension' ? 'Extension PO Number *' : 'PO Number *'}</Label>
            <Input
              id="po_number"
              value={formData.po_number}
              onChange={(e) => setFormData({ ...formData, po_number: e.target.value.toUpperCase() })}
              placeholder="PO-2025-001"
              data-testid="po-number-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="short_title">Short Title</Label>
            <Input
              id="short_title"
              value={formData.short_title || ''}
              onChange={(e) => setFormData({ ...formData, short_title: e.target.value })}
              placeholder="e.g. FY26 Azure Training"
              data-testid="po-short-title-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="total_amount">{formData.po_type === 'extension' ? 'Extension Amount *' : 'Total Amount *'}</Label>
            <Input
              id="total_amount"
              type="number"
              min="0"
              step="0.01"
              value={formData.total_amount}
              onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
              placeholder="100000.00"
              data-testid="po-amount-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="currency">Currency</Label>
            <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="INR">INR</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="issue_date">Issue Date *</Label>
            <DatePicker
              value={formData.issue_date}
              onChange={(v) => setFormData({ ...formData, issue_date: v })}
              placeholder="Select issue date"
              testId="po-issue-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expiry_date">Expiry Date</Label>
            <DatePicker
              value={formData.expiry_date}
              onChange={(v) => setFormData({ ...formData, expiry_date: v })}
              placeholder="Select expiry date"
              testId="po-expiry-date-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sow_reference">SOW Reference</Label>
          <Input
            id="sow_reference"
            value={formData.sow_reference}
            onChange={(e) => setFormData({ ...formData, sow_reference: e.target.value })}
            placeholder="SOW-2025-001"
            data-testid="po-sow-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ms_owner">Microsoft Owner</Label>
          <Input
            id="ms_owner"
            value={formData.ms_owner || ''}
            onChange={(e) => setFormData({ ...formData, ms_owner: e.target.value })}
            placeholder="e.g. Jane Doe (Microsoft)"
            data-testid="po-ms-owner-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Description of PO scope..."
            rows={2}
            data-testid="po-description-input"
          />
        </div>

        <POLineItemsEditor
          lineItems={formData.line_items || []}
          onChange={(items) => setFormData({ ...formData, line_items: items })}
        />

        <div className="border-t pt-4">
          <EntityCustomFields
            entityType={isContentWorkload ? 'content_po' : 'purchase_order'}
            values={formData.custom_fields || {}}
            onChange={(v) => setFormData({ ...formData, custom_fields: v })}
            isSuperAdmin={user?.role === 'superadmin'}
            currentUser={user}
            onSchemaChanged={onSchemaChanged}
            testPrefix="po-custom-field"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="save-po-button">
            {editingPo ? 'Update' : formData.po_type === 'extension' ? 'Add Extension' : isContentWorkload ? 'Create Content PO' : 'Create'}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
  );
};

export default POFormDialog;
